import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Company } from "@/types";
import { reactive } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

/**
 * Composable for Polish Power of Attorney generation and management.
 * Handles PoA generation state, validation, and file download functionality.
 */
export function usePolishPoA(company: Company) {
  const { globalErrorHandler } = useGlobalHandlers();

  // PoA generation state (device-specific)
  const poaGenerationStates = reactive(new Map<number, boolean>());
  const poaGeneratedSuccessfully = reactive(new Map<number, boolean>());

  /**
   * Get PoA document status for a device.
   * Since new flow generates PDFs directly without creating Document records,
   * this always returns "none" for new generations.
   */
  const getDevicePoAStatus = (_deviceId: number): string => {
    // New direct PDF generation flow doesn't create Document records
    return "none";
  };

  /**
   * Check if device can generate PoA (all Polish fields present)
   */
  const canGeneratePoA = (registryDevice: any): boolean => {
    // Check if all required Polish fields are present in country_specific_metadata
    const polishMetadata = registryDevice.country_specific_metadata;
    if (!polishMetadata) {
      return false;
    }
    return (
      polishMetadata.installation_address &&
      polishMetadata.metering_system_number &&
      polishMetadata.metering_system_multiplier &&
      polishMetadata.osd_name &&
      polishMetadata.osd_address
    );
  };

  /**
   * Generate PoA for Polish device - downloads immediately
   */
  const generatePoA = async (deviceId: number): Promise<void> => {
    if (company.country !== "PL") {
      console.error("PoA generation is only available for Polish companies");
      return;
    }

    if (!company.id) {
      console.error("Company ID is required for PoA generation");
      return;
    }

    try {
      poaGenerationStates.set(deviceId, true);

      // Call endpoint which generates and returns the file directly
      const response = await goSolidApi.post(
        `/issuance-reporting/companies/${company.id}/registry-devices/${deviceId}/pl/metering-data-poa`,
        {},
        {
          responseType: "blob",
        }
      );

      if (response.status >= 200 && response.status < 300) {
        // Extract filename from Content-Disposition header if available
        const contentDisposition = response.headers["content-disposition"];
        let filename = `PoA_Device_${deviceId}.pdf`;

        if (contentDisposition) {
          const filenameMatch =
            contentDisposition.match(/filename="?(.+?)"?$/i);
          if (filenameMatch) {
            filename = filenameMatch[1].replace(/['"]/g, "").trim();
          }
        }

        // Create blob and trigger download
        const fileBlob = new Blob([response.data], {
          type: "application/pdf",
        });
        const fileURL = window.URL.createObjectURL(fileBlob);

        const downloadLink = document.createElement("a");
        downloadLink.href = fileURL;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(fileURL);

        // Mark as successfully generated
        poaGeneratedSuccessfully.set(deviceId, true);
      }
    } catch (err: any) {
      globalErrorHandler(err);
    } finally {
      poaGenerationStates.set(deviceId, false);
    }
  };

  return {
    poaGenerationStates,
    poaGeneratedSuccessfully,
    getDevicePoAStatus,
    canGeneratePoA,
    generatePoA,
  };
}
